// ----- Phase 5.25.4 — Shared Import Preview Panel -----
//
// V1 reference: `~/autoectd/src/screens/ImportScreen.jsx`, the "Import
// Preview" block (renders when `parsed && !importResult`). V1 has three
// upload paths (XML / ZIP / openFDA) and all three converge on this
// same metadata-confirmation panel + "Import N Sequences" commit button.
//
// This file extracts that V1 panel as a self-contained, pure React
// component so the three V2 sub-phases (5.25.1 XML, 5.25.2 ZIP,
// 5.25.3 FDA ESG) can import it without each re-building the same
// editable-metadata + sequence-list + commit UI.
//
// The component is intentionally NOT wired to any network call: it
// accepts parsed data as props, fires `onChange` while the user edits
// metadata, and invokes `onCommit` with the (possibly-edited) data
// when the user clicks the commit button. The actual `POST /api/v1/import/commit`
// call ships in Phase 5.25.5.
//
// V1 field names (from `~/autoectd/src/screens/ImportScreen.jsx`):
//   summary.drugName / applicationNumber / applicant — metadata seed
//   summary.totalDocuments — count display
//   summary.format         — "eCTD 3.2.2" | "eCTD 4.0" | "fda-esg"
//   sequences[i].sequenceNumber / submissionType / submissionDate /
//                 submissionStatus / documents (array)
// V1 had `documents: SomeDoc[]` per sequence; V2's contract uses the
// lighter `documentCount?: number` since the panel only renders a
// count. V1's `date` field (used in parsed sequences only, after the
// FDA result is mapped to the commit shape) is normalized to
// `submissionDate` here — V2 carries the same field name end-to-end.

import { useMemo, type CSSProperties, type ReactElement } from 'react';

// ----- Public types ----------------------------------------------------------

export interface ImportSequence {
  readonly sequenceNumber: string;
  readonly submissionType: string;
  readonly submissionDate?: string;
  readonly submissionStatus?: string;
  readonly documentCount?: number;
}

export interface ImportParsedData {
  readonly drugName: string;
  readonly applicationNumber: string;
  readonly applicant: string;
  readonly indication?: string;
  readonly sequences: readonly ImportSequence[];
  readonly summary: {
    readonly totalDocuments: number;
    readonly format: 'eCTD 3.2.2' | 'eCTD 4.0' | string;
    readonly source: 'xml' | 'zip' | 'fda';
  };
}

export interface ImportPreviewPanelProps {
  readonly parsedData: ImportParsedData;
  readonly isCommitting?: boolean;
  readonly onChange?: (next: ImportParsedData) => void;
  readonly onCommit?: (data: ImportParsedData) => void;
  readonly onCancel?: () => void;
}

// ----- Styles (match NewSubmissionPage / Phase 5.26 wizard conventions) -----

const cardStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '20px 22px',
  marginBottom: 16,
};

const sectionLabelStyle: CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted)',
  fontWeight: 700,
  margin: '0 0 12px 0',
};

const summaryGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 12,
  marginBottom: 20,
};

const summaryItemStyle: CSSProperties = {
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '10px 12px',
};

const summaryItemLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted)',
  fontWeight: 600,
  marginBottom: 4,
};

const summaryItemValueStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text)',
};

const formGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 12,
  marginTop: 12,
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 4,
};

const requiredMark: CSSProperties = { color: 'var(--red, #b91c1c)', marginLeft: 2 };

const seqListStyle: CSSProperties = {
  marginTop: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxHeight: 360,
  overflowY: 'auto',
};

const seqRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '80px 1fr 100px 90px 90px',
  alignItems: 'center',
  gap: 12,
  padding: '8px 10px',
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
};

const seqNumStyle: CSSProperties = {
  fontFamily: 'monospace',
  fontWeight: 600,
};

const seqMetaStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 12,
};

const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
  marginTop: 8,
};

const primaryButtonStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '10px 24px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const primaryButtonDisabledStyle: CSSProperties = {
  ...primaryButtonStyle,
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
  cursor: 'not-allowed',
};

const secondaryButtonStyle: CSSProperties = {
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const emptyStateStyle: CSSProperties = {
  textAlign: 'center',
  padding: '32px 16px',
  fontSize: 13,
  color: 'var(--text-muted)',
  background: 'var(--surface2)',
  border: '1px dashed var(--border)',
  borderRadius: 6,
};

// ISS-033 — honest-states notice. Import creates metadata shells only (no
// document files/contents), and for openFDA lookups the public submission
// history is NOT proof of eCTD sequence identity or of a successful agency
// transmission. Duplicate history (e.g. a public ORIG and SUPPL that share a
// sequence number) must be reconciled, not silently renumbered.
const noticeStyle: CSSProperties = {
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderLeft: '3px solid var(--amber, #b45309)',
  borderRadius: 6,
  padding: '10px 12px',
  marginBottom: 16,
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--text-muted)',
};

const noticeTitleStyle: CSSProperties = {
  fontWeight: 700,
  color: 'var(--text)',
  marginBottom: 4,
};

// ----- Component -------------------------------------------------------------

const SOURCE_LABEL: Record<ImportParsedData['summary']['source'], string> = {
  xml: 'XML Backbone Upload',
  zip: 'eCTD ZIP Package',
  fda: 'openFDA Lookup',
};

export function ImportPreviewPanel({
  parsedData,
  isCommitting = false,
  onChange,
  onCommit,
  onCancel,
}: ImportPreviewPanelProps): ReactElement {
  // Required-field validation: drugName / applicationNumber / applicant are
  // all required for a valid commit. Indication is optional (V1 leaves it
  // blank for openFDA-sourced imports and that's allowed). Memoized so the
  // commit button doesn't recompute disabled state on every keystroke in
  // unrelated fields. Hoisted ABOVE the empty-state branch so hooks fire
  // in a stable order regardless of which path renders.
  const requiredOk = useMemo(
    () =>
      parsedData.drugName.trim() !== '' &&
      parsedData.applicationNumber.trim() !== '' &&
      parsedData.applicant.trim() !== '',
    [parsedData.drugName, parsedData.applicationNumber, parsedData.applicant],
  );

  // ISS-033 — honest-states notice, shared by the empty-state and the main
  // preview. Always states that only metadata shells are created (document
  // files/contents are not imported — a known capability gap). For openFDA
  // lookups it additionally warns that public history is not proof of eCTD
  // sequence identity or agency transmission and that duplicates need
  // reconciliation, not silent renumbering.
  const notice = (
    <div data-testid="import-preview-notice" style={noticeStyle}>
      <div style={noticeTitleStyle}>Before you import</div>
      <div>
        This creates submission <strong>metadata records only</strong> — document files and their
        contents are <strong>not</strong> imported.
      </div>
      {parsedData.summary.source === 'fda' ? (
        <div style={{ marginTop: 6 }} data-testid="import-preview-notice-fda">
          Public FDA submission history is a record of agency correspondence, not proof of eCTD
          sequence identity or of a successful agency transmission. Public submission numbers (e.g. an
          ORIG and a SUPPL) can collide on the same sequence; duplicates must be reconciled before
          import — this tool will not silently renumber public history into eCTD sequence identities.
        </div>
      ) : null}
    </div>
  );

  // Empty-state guard — parsers may produce zero sequences on edge-case input.
  if (parsedData.sequences.length === 0) {
    return (
      <div data-testid="import-preview-panel" style={cardStyle}>
        <h2 style={{ margin: '0 0 12px 0', fontSize: 16 }}>Import Preview</h2>
        {notice}
        <div data-testid="import-preview-empty" style={emptyStateStyle}>
          No sequences found in the imported data.
          {parsedData.summary.source === 'xml' &&
            ' Verify the XML files contain eCTD backbone elements.'}
          {parsedData.summary.source === 'zip' &&
            ' Verify the ZIP contains an eCTD folder structure.'}
          {parsedData.summary.source === 'fda' &&
            ' Try a different application number or use XML/ZIP import.'}
        </div>
        {onCancel ? (
          <div style={footerStyle}>
            <button
              type="button"
              data-testid="import-preview-cancel"
              onClick={onCancel}
              style={secondaryButtonStyle}
            >
              Cancel
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const update = <K extends keyof ImportParsedData>(key: K, value: ImportParsedData[K]): void => {
    if (!onChange) return;
    onChange({ ...parsedData, [key]: value });
  };

  const publicHistory = parsedData.summary.source === 'fda';
  const sequenceCount = parsedData.sequences.length;
  const buttonLabel = publicHistory
    ? 'Public history cannot be imported'
    : isCommitting
      ? 'Importing metadata…'
      : `Import ${sequenceCount} Sequence${sequenceCount === 1 ? '' : 's'} (metadata only)`;
  const buttonDisabled = publicHistory || !onCommit || isCommitting || !requiredOk;

  return (
    <div data-testid="import-preview-panel" style={cardStyle}>
      <h2 style={{ margin: '0 0 4px 0', fontSize: 16 }}>{publicHistory ? 'Public FDA history' : 'Import Preview'}</h2>
      <p
        data-testid="import-preview-source"
        style={{ margin: '0 0 16px 0', fontSize: 12, color: 'var(--text-muted)' }}
      >
        Source: {SOURCE_LABEL[parsedData.summary.source]}
      </p>

      {notice}

      <p data-testid="import-scope-notice" style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        {publicHistory
          ? 'Public FDA action numbers are not verified eCTD sequence numbers. These records are for reference and cannot be imported as submission sequences.'
          : 'This imports metadata as draft sequence records only. Document files are not imported, and no compilation or validation is established. Upload the source documents before continuing.'}
      </p>

      {/* Summary tiles (read-only) */}
      <div style={summaryGridStyle}>
        <div style={summaryItemStyle} data-testid="import-preview-summary-format">
          <div style={summaryItemLabelStyle}>Format Detected</div>
          <div style={summaryItemValueStyle}>{publicHistory ? 'Public FDA records' : parsedData.summary.format || '—'}</div>
        </div>
        <div style={summaryItemStyle} data-testid="import-preview-summary-sequences">
          <div style={summaryItemLabelStyle}>{publicHistory ? 'Public actions found' : 'Sequences Found'}</div>
          <div style={summaryItemValueStyle}>{sequenceCount}</div>
        </div>
        <div style={summaryItemStyle} data-testid="import-preview-summary-documents">
          <div style={summaryItemLabelStyle}>Documents Found</div>
          <div style={summaryItemValueStyle}>{parsedData.summary.totalDocuments}</div>
        </div>
      </div>

      {/* Editable metadata grid */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <p style={sectionLabelStyle}>Confirm Metadata</p>
        <div style={formGridStyle}>
          <label>
            <span style={labelStyle}>
              Drug Name
              <span style={requiredMark}>*</span>
            </span>
            <input
              data-testid="import-preview-field-drugName"
              type="text"
              value={parsedData.drugName}
              onChange={(e) => update('drugName', e.target.value)}
              placeholder="e.g. Zeltrexin 50mg"
              style={inputStyle}
            />
          </label>

          <label>
            <span style={labelStyle}>
              Application Number
              <span style={requiredMark}>*</span>
            </span>
            <input
              data-testid="import-preview-field-applicationNumber"
              type="text"
              value={parsedData.applicationNumber}
              onChange={(e) => update('applicationNumber', e.target.value)}
              placeholder="e.g. NDA-225431"
              style={inputStyle}
            />
          </label>

          <label>
            <span style={labelStyle}>
              Applicant
              <span style={requiredMark}>*</span>
            </span>
            <input
              data-testid="import-preview-field-applicant"
              type="text"
              value={parsedData.applicant}
              onChange={(e) => update('applicant', e.target.value)}
              placeholder="e.g. Helix Pharmaceuticals Inc."
              style={inputStyle}
            />
          </label>

          <label>
            <span style={labelStyle}>Indication</span>
            <input
              data-testid="import-preview-field-indication"
              type="text"
              value={parsedData.indication ?? ''}
              onChange={(e) => update('indication', e.target.value)}
              placeholder="e.g. Treatment of plaque psoriasis"
              style={inputStyle}
            />
          </label>
        </div>
      </div>

      {/* Sequence list (read-only) */}
      <div style={{ marginTop: 20 }}>
        <p style={sectionLabelStyle}>{publicHistory ? 'Public action records' : 'Sequence metadata to import'} ({sequenceCount})</p>
        <div role="list" data-testid="import-preview-sequence-list" style={seqListStyle}>
          {parsedData.sequences.map((seq, idx) => (
            <div
              key={`${seq.sequenceNumber}-${idx}`}
              role="listitem"
              data-testid={`import-preview-sequence-row-${idx}`}
              style={seqRowStyle}
            >
              <span style={seqNumStyle} data-testid={`import-preview-sequence-number-${idx}`}>
                {seq.sequenceNumber || '—'}
              </span>
              <span data-testid={`import-preview-sequence-type-${idx}`}>
                {seq.submissionType || '—'}
              </span>
              <span style={seqMetaStyle}>
                {seq.submissionDate ? seq.submissionDate.slice(0, 10) : '—'}
              </span>
              <span style={seqMetaStyle}>{seq.submissionStatus ?? '—'}</span>
              <span style={seqMetaStyle}>
                {typeof seq.documentCount === 'number' ? `${seq.documentCount} docs` : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer with optional Cancel + primary Commit */}
      <div style={footerStyle}>
        {onCancel ? (
          <button
            type="button"
            data-testid="import-preview-cancel"
            onClick={onCancel}
            disabled={isCommitting}
            style={isCommitting ? primaryButtonDisabledStyle : secondaryButtonStyle}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          data-testid="import-preview-commit"
          onClick={() => { if (!buttonDisabled) onCommit?.(parsedData); }}
          disabled={buttonDisabled}
          style={buttonDisabled ? primaryButtonDisabledStyle : primaryButtonStyle}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}
